import React from 'react';
import { Container } from '../../components/components';
import { Row, Col, Skeleton } from 'antd';
import { DollarOutlined } from '@ant-design/icons';
import { FaDollarSign } from 'react-icons/fa';
import { useGetIncomeStatsQuery } from '../../services/stats';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import dayjs from 'dayjs';

const Income = () => {
  const { data, isLoading } = useGetIncomeStatsQuery(undefined, {
    refetchOnMountOrArgChange: true,
  });

  const incomeData = data?.data;

  const currentMonth = dayjs().format('MMMM');
  const currentYear = dayjs().format('YYYY');

  const formatCurrency = value => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(value || 0);
  };

  return (
    <Container
      selected={'10'}
      header={
        <div className="flex items-center">
          <div className="bg-primary h-9 w-9 rounded-full flex justify-center items-center">
            <DollarOutlined className="text-white text-lg" />
          </div>
          <p className="ml-3 font-semibold text-2xl">Income</p>
        </div>
      }
    >
      <div className="bg-white p-8 rounded">
        {isLoading ? (
          <Skeleton active paragraph={{ rows: 6 }} />
        ) : (
          <div>
            <Row gutter={{ xs: 8, sm: 16, md: 24, lg: 32 }}>
              <Col lg={8} md={12} xs={24}>
                <div className="bg-white p-7 rounded-3xl h-40 flex flex-col justify-between shadow-md">
                  <div>
                    <FaDollarSign className="text-4xl text-green-600" />
                  </div>
                  <div className="flex justify-between items-center">
                    <div>
                      <p className="text-base font-semibold">Month to Date</p>
                      <p className="text-gray-500 text-sm">{currentMonth}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-green-600 text-3xl font-semibold">
                        {formatCurrency(incomeData?.monthToDate?.total)}
                      </p>
                      <p className="text-gray-500 text-sm">
                        {incomeData?.monthToDate?.count || 0} paid
                      </p>
                    </div>
                  </div>
                </div>
              </Col>
              <Col lg={8} md={12} xs={24}>
                <div className="bg-white p-7 rounded-3xl h-40 flex flex-col justify-between shadow-md">
                  <div>
                    <FaDollarSign className="text-4xl text-blue-600" />
                  </div>
                  <div className="flex justify-between items-center">
                    <div>
                      <p className="text-base font-semibold">Year to Date</p>
                      <p className="text-gray-500 text-sm">{currentYear}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-blue-600 text-3xl font-semibold">
                        {formatCurrency(incomeData?.yearToDate?.total)}
                      </p>
                      <p className="text-gray-500 text-sm">
                        {incomeData?.yearToDate?.count || 0} paid
                      </p>
                    </div>
                  </div>
                </div>
              </Col>
              <Col lg={8} md={24} xs={24}>
                <div className="bg-white p-7 rounded-3xl h-40 flex flex-col justify-between shadow-md">
                  <div>
                    <FaDollarSign className="text-4xl text-red-500" />
                  </div>
                  <div className="flex justify-between items-center">
                    <div>
                      <p className="text-base font-semibold">Outstanding</p>
                      <p className="text-gray-500 text-sm">This month</p>
                    </div>
                    <div className="text-right">
                      <p className="text-red-500 text-3xl font-semibold">
                        {formatCurrency(incomeData?.outstanding?.total)}
                      </p>
                      <p className="text-gray-500 text-sm">
                        {incomeData?.outstanding?.count || 0} unpaid
                      </p>
                    </div>
                  </div>
                </div>
              </Col>
            </Row>

            <div className="mt-8 bg-white p-6 rounded-3xl shadow-md">
              <h3 className="text-lg font-semibold mb-4">
                Monthly Income Breakdown ({currentYear})
              </h3>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart
                  data={incomeData?.yearToDate?.monthlyBreakdown || []}
                  margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis
                    tickFormatter={value => `$${value.toLocaleString()}`}
                  />
                  <Tooltip
                    formatter={value => [formatCurrency(value), 'Income']}
                    labelStyle={{ fontWeight: 'bold' }}
                  />
                  <Bar dataKey="total" fill="#4CAF50" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}
      </div>
    </Container>
  );
};

export default Income;
